import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { filterClasses } from "@swivl-mono/helpers";
import { CSSProperties } from "react";
import { memo } from "react";
import type { MouseEventHandler } from "react";

import "./Checkbox.scss";

enum Outlet {
  WRAPPER = "wrapper",
  CHECKBOX = "checkbox",
  LABEL = "label"
}
interface CheckboxProps {
  classNames?: { [Key in Outlet]?: string };
  checked?: CheckboxPrimitive.CheckedState | boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  isThemed?: boolean;
  label?: string;
  name?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  onCheckedChange?: (checked: CheckboxPrimitive.CheckedState) => void;
  style?: CSSProperties;
}

const BaseCheckbox = ({
  checked,
  classNames = {},
  defaultChecked = false,
  disabled = false,
  isThemed = false,
  label,
  onClick,
  name = "checkbox",
  onCheckedChange,
  style
}: CheckboxProps) => {
  const internalClassNames = {
    wrapper: filterClasses("Checkbox flex items-center gap-2", disabled && "is-disabled", isThemed && "is-themed", classNames.wrapper),
    checkbox: filterClasses(
      "checkbox-root flex size-5 cursor-pointer items-center justify-center rounded-[3px] border transition-colors",
      classNames.checkbox
    ),
    // No `flex-1`: a stretched label stays clickable far past its text, so the
    // hit area has to end where the words do.
    label: filterClasses("checkbox-label cursor-pointer text-left", classNames.label)
  };

  return (
    <div className={internalClassNames.wrapper} aria-disabled={disabled} style={style}>
      <CheckboxPrimitive.Root
        checked={checked}
        className={internalClassNames.checkbox}
        defaultChecked={defaultChecked}
        disabled={disabled}
        id={name}
        name={name}
        onCheckedChange={onCheckedChange}
        onClick={onClick}
      />
      {label && (
        <label className={internalClassNames.label} aria-disabled={disabled} htmlFor={name} style={{ margin: 0 }}>
          {label}
        </label>
      )}
    </div>
  );
};

export const Checkbox = memo(BaseCheckbox);
